"use client";

/**
 * Fetches Predexon-powered enrichment for PlaceTrade V2:
 *   - Smart money positioning (top wallets YES/NO lean)
 *   - Recent live trades (for the ticker)
 *
 * Both endpoints live in the FrenFlow backend and proxy to Predexon internally.
 * Hook degrades gracefully: if an endpoint 5xx's or Predexon is disabled
 * (Dev+ plan required for smart money), the corresponding widget just
 * doesn't render — no loud UI breakage.
 */

import { useEffect, useState } from "react";
import type { LiveTrade, Outcome, SmartMoney } from "../types";

// Predexon outage: no llamar al endpoint de smart money (cae en catch silencioso,
// pero mejor no gastar la request). Los live trades NO dependen de Predexon.
const HIDE_PREDEXON = process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS === "true";

interface PredexonSmartMoneyShape {
  condition_id: string;
  net_positioning: number;
  smart_wallets_yes: number;
  smart_wallets_no: number;
  total_smart_volume: number;
  top_holders: Array<{ address: string; side: "YES" | "NO"; shares: number; primary_style: string }>;
}

interface PolymarketTradeShape {
  proxyWallet?: string;
  user?: string;
  name?: string;
  pseudonym?: string;
  profileImage?: string;
  profileImageOptimized?: string;
  outcome?: string;
  outcomeIndex?: number;
  side?: "BUY" | "SELL" | string;
  size?: number | string;
  price?: number | string;
  timestamp?: number | string;
  transactionHash?: string;
}

// Module-level cache so we don't hit /api/traders/:wallet more than once
// per wallet across the whole session. Keyed by lowercased address.
const traderUpgradeCache = new Map<string, { username?: string; avatar?: string | null }>();

/**
 * Legacy /api/analytics/market/:cid/smart returns the PredexonSmartMoney shape
 * (net_positioning, smart_wallets_yes/no, total_smart_volume, top_holders).
 * Same mapping logic works — reuse mapSmartMoney.
 */
function mapLegacySmartMoney(raw: PredexonSmartMoneyShape | null | undefined): SmartMoney | null {
  return mapSmartMoney(raw);
}

function mapSmartMoney(raw: PredexonSmartMoneyShape | null | undefined): SmartMoney | null {
  if (!raw) return null;
  const yesW = Number(raw.smart_wallets_yes || 0);
  const noW = Number(raw.smart_wallets_no || 0);
  const total = yesW + noW;
  // Only require 2+ wallets. Social proof loses weight with weak samples,
  // but hiding the widget entirely (what the old 55% gate did) means users
  // never see the signal on markets that happen to be close to 50/50 —
  // and those are exactly the ones where the widget has the most nudge
  // value. Let the UI render the pct honestly.
  if (total < 2) return null;
  const yesPct = (yesW / total) * 100;
  const leansYes = yesPct >= 50;
  const dominantPct = leansYes ? yesPct : 100 - yesPct;
  const avgTicket =
    total > 0 && raw.total_smart_volume > 0
      ? Math.max(1, Math.round(raw.total_smart_volume / total / 100) * 100)
      : 25;
  return {
    side: leansYes ? "yes" : "no",
    pct: Math.round(dominantPct),
    wallets: total,
    avgTicket: Math.min(500, avgTicket),
  };
}

function mapTrade(t: PolymarketTradeShape): LiveTrade | null {
  const wallet = (t.proxyWallet || t.user || "").toLowerCase();
  // Prefer display names; only fall back to wallet hex if no name set.
  const user = t.name || t.pseudonym || wallet || "";
  if (!user) return null;
  const sizeNum = typeof t.size === "string" ? parseFloat(t.size) : t.size ?? 0;
  const priceNum = typeof t.price === "string" ? parseFloat(t.price) : t.price ?? 0;
  if (!sizeNum || !priceNum) return null;

  const outcomeRaw = (t.outcome ?? "").toString().toLowerCase();
  let side: Outcome = "yes";
  if (outcomeRaw === "no") side = "no";
  else if (outcomeRaw === "yes") side = "yes";
  else if (typeof t.outcomeIndex === "number") side = t.outcomeIndex === 0 ? "yes" : "no";

  const actionRaw = (t.side ?? "").toString().toUpperCase();
  const action: "buy" | "sell" = actionRaw === "SELL" ? "sell" : "buy";

  const tsNum = typeof t.timestamp === "string" ? parseFloat(t.timestamp) : t.timestamp ?? 0;
  const tsMs = tsNum > 1e12 ? tsNum : tsNum * 1000;

  const avatar = t.profileImageOptimized || t.profileImage || null;

  return {
    user,
    wallet: wallet || undefined,
    avatar,
    side,
    action,
    size: sizeNum,
    price: priceNum,
    ts: new Date(tsMs).toISOString(),
  };
}

interface EnrichmentResult {
  smartMoney: SmartMoney | null;
  liveTrades: LiveTrade[];
}

async function fetchTraderProfile(wallet: string): Promise<{ username?: string; avatar?: string | null } | null> {
  const key = wallet.toLowerCase();
  if (traderUpgradeCache.has(key)) return traderUpgradeCache.get(key) ?? null;
  try {
    const r = await fetch(`/api/traders/${key}`);
    if (!r.ok) {
      traderUpgradeCache.set(key, {});
      return {};
    }
    const body = await r.json();
    const t = body?.trader ?? body?.data ?? body;
    const upgrade = {
      username: (t?.username as string | undefined) || undefined,
      avatar: (t?.avatarOptimized as string | null | undefined) || (t?.avatar as string | null | undefined) || null,
    };
    traderUpgradeCache.set(key, upgrade);
    return upgrade;
  } catch {
    traderUpgradeCache.set(key, {});
    return {};
  }
}

async function upgradeProfiles(
  trades: LiveTrade[],
  onUpdate: (next: LiveTrade[]) => void,
) {
  const wallets = Array.from(new Set(trades.map((t) => t.wallet).filter((w): w is string => !!w)));
  if (wallets.length === 0) return;
  const profiles = await Promise.all(wallets.map((w) => fetchTraderProfile(w).then((p) => ({ w, p }))));
  const byWallet = new Map(profiles.map(({ w, p }) => [w, p]));
  const enriched = trades.map((t) => {
    if (!t.wallet) return t;
    const upgrade = byWallet.get(t.wallet);
    if (!upgrade) return t;
    return {
      ...t,
      user: upgrade.username || t.user,
      avatar: upgrade.avatar ?? t.avatar,
    };
  });
  onUpdate(enriched);
}

export function usePlaceTradeEnrichment(conditionId?: string | null): EnrichmentResult {
  const [smartMoney, setSmartMoney] = useState<SmartMoney | null>(null);
  const [liveTrades, setLiveTrades] = useState<LiveTrade[]>([]);

  useEffect(() => {
    if (!conditionId) {
      setSmartMoney(null);
      setLiveTrades([]);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();

    const fetchSmart = async () => {
      try {
        // Same endpoint the legacy panel uses (useSmartMoney hook), has a
        // Next.js proxy in /app/api/analytics/market/[conditionId]/smart.
        const r = await fetch(`/api/analytics/market/${conditionId}/smart`, { signal: ctrl.signal });
        if (!r.ok) return;
        const body = await r.json();
        if (cancelled) return;
        if (body?.success && body.data) {
          // Legacy endpoint returns the mapped shape directly (not Predexon raw),
          // so we adapt it to what SmartMoneyInline expects.
          setSmartMoney(mapLegacySmartMoney(body.data));
        }
      } catch {
        // Silent — widget simply hides if smart money unavailable.
      }
    };

    const fetchTrades = async () => {
      try {
        // Same proxy the legacy RecentTrades uses. Returns an array directly
        // (not wrapped in `{data:[]}`).
        const r = await fetch(`/api/polymarket/trades?market=${conditionId}&limit=20`, { signal: ctrl.signal });
        if (!r.ok) return;
        const body = await r.json();
        const raw: PolymarketTradeShape[] = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
        if (cancelled) return;
        const mapped = raw.map(mapTrade).filter((t): t is LiveTrade => t !== null);
        const top = mapped.slice(0, 10);
        setLiveTrades(top);
        // Fire-and-forget profile upgrade: if our traders DB has a FrenFlow
        // username / better avatar for any of these wallets, overlay it on
        // the ticker so traders see social identity instead of anonymous
        // Polymarket pseudonyms.
        upgradeProfiles(top, (enriched) => {
          if (!cancelled) setLiveTrades(enriched);
        });
      } catch {
        // Silent — LiveTradeTicker hides with empty array.
      }
    };

    if (!HIDE_PREDEXON) fetchSmart();
    fetchTrades();
    const id = setInterval(fetchTrades, 15_000);

    return () => {
      cancelled = true;
      ctrl.abort();
      clearInterval(id);
    };
  }, [conditionId]);

  return { smartMoney, liveTrades };
}
